import { ACCREDITATION_ROLES } from '@r-and-a-shared-ui/utils';
import {
  getPPAuthToken,
  IMPLICIT_NOMINATED_FOR_DINNER_ROLES,
  ResponseModel,
  stringToBoolean,
} from '@r-and-a-shared-ui/utils';
import { ActionHandlerParams } from '../../types';
import {
  PP_CreateAccreditationContactInputSchema,
  pp_accreditationFormDefaultValuesUpdate,
} from '@r-and-a-shared-ui/randa-trpc-server/input-schemas';
import { Country, getCountryCallingCode } from 'react-phone-number-input';
import { pp_genericMutationHandlerWithFormValidation } from '../pp-form-action-mutation-handlers';

const MAX_NOMINATED_DINNER_MESSAGE =
  'Maximum number of nominated dinner accreditations has been reached.';

const MAX_APPLICATIONS_PER_ROLE_MESSAGE =
  'The maximum number of accreditations with role <role> has been reached.';

const getCountryName = (countryCode: string): string => {
  try {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return displayNames.of(countryCode) || countryCode;
  } catch {
    return countryCode;
  }
};

export const handle = async (
  params: ActionHandlerParams,
): Promise<ResponseModel> => {
  const authTokenMutation = params.authTokenMutation;

  if (authTokenMutation) {
    params.bearerToken = await getPPAuthToken(authTokenMutation?.mutateAsync);
  }

  setAdditionalFields(params.submitData);

  if (exceedsRoleLimit(params)) {
    const roleLimitMessage = MAX_APPLICATIONS_PER_ROLE_MESSAGE.replace(
      '<role>',
      params.submitData?.accreditationForm_jobTitle,
    );

    params.setError?.('root.serverError', {
      type: 'manual',
      message: roleLimitMessage,
    });

    return {
      hasErrors: true,
      errorMessages: [roleLimitMessage],
    };
  }

  if (exceedsDinnerNomineeLimit(params)) {
    params.setError?.('root.serverError', {
      type: 'manual',
      message: MAX_NOMINATED_DINNER_MESSAGE,
    });

    return {
      hasErrors: true,
      errorMessages: [MAX_NOMINATED_DINNER_MESSAGE],
    };
  }

  const resultData = await pp_genericMutationHandlerWithFormValidation(
    params,
    PP_CreateAccreditationContactInputSchema,
    pp_accreditationFormDefaultValuesUpdate,
  );
  if (resultData?.hasErrors) {
    return resultData;
  }

  return resultData;
};

const setAdditionalFields = (submitData: any) => {
  const subCategoryIndex = Number(submitData?.accreditationForm_subCategory);
  const countryCode = submitData?.accreditationForm_mobileCountryCode;
  const countryCallingCode = countryCode
    ? getCountryCallingCode(countryCode as Country)
    : '';
  const mobileNumber = submitData?.accreditationForm_mobile;

  submitData.accreditationForm_fullName = `${
    submitData.accreditationForm_forename || ''
  } ${submitData.accreditationForm_surname || ''}`.trim();

  submitData.accreditationForm_nominatedForDinner = getNominatedForDinnerValue(
    submitData.accreditationForm_subCategory,
    submitData.accreditationForm_nominatedForDinnerEnabled,
  );

  submitData.accreditationForm_affiliatedOrganization =
    submitData.championshipPlayer;

  submitData.accreditationForm_jobTitle =
    Object.values(ACCREDITATION_ROLES).find(
      role => role.value === subCategoryIndex,
    )?.label || '';

  submitData.accreditationForm_mobileCode = countryCode
    ? `${getCountryName(countryCode)} (+${countryCallingCode})`
    : '';

  submitData.accreditationForm_mobile = mobileNumber
    ? mobileNumber.replace(`+${countryCallingCode}`, '')
    : '';

  submitData.currentChampionshipId = submitData?.currentChampionshipId || null;
};

const getNominatedForDinnerValue = (
  subCategory: string,
  nominatedForDinnerEnabled: boolean,
) => {
  const role = Number(subCategory);
  const isImplicitNominatedRole =
    Number.isFinite(role) && IMPLICIT_NOMINATED_FOR_DINNER_ROLES.includes(role);

  return isImplicitNominatedRole || nominatedForDinnerEnabled;
};

const exceedsDinnerNomineeLimit = (params: ActionHandlerParams): boolean => {
  const maxNumberOfNominatedForDinner =
    params.formContentProps?.maxNumberOfNominatedForDinner;
  const currentNominatedForDinnerCount =
    params.formContentProps?.currentNominatedForDinnerCount ?? 0;

  const submitData = params?.submitData;
  const subCategoryValue = submitData?.accreditationForm_subCategory;
  const nominatedForDinnerValue =
    submitData?.accreditationForm_nominatedForDinner;

  const role = Number(subCategoryValue);

  const isImplicitNominatedRole =
    Number.isFinite(role) && IMPLICIT_NOMINATED_FOR_DINNER_ROLES.includes(role);

  if (isImplicitNominatedRole) {
    return false;
  }

  if (
    typeof maxNumberOfNominatedForDinner !== 'number' ||
    !Number.isFinite(maxNumberOfNominatedForDinner) ||
    maxNumberOfNominatedForDinner < 0
  ) {
    return false;
  }

  const shouldNominateForDinner = stringToBoolean(nominatedForDinnerValue?.[0]);

  if (!shouldNominateForDinner) {
    return false;
  }

  return currentNominatedForDinnerCount + 1 > maxNumberOfNominatedForDinner;
};

const exceedsRoleLimit = (params: ActionHandlerParams): boolean => {
  const maxNumberOfApplicationsPerRole =
    params.formContentProps?.maxNumberOfApplicationsPerRole;
  const currentApplicationsPerRoleCount =
    params.formContentProps?.currentApplicationsPerRoleCount ?? {};

  const submitData = params?.submitData;
  const subCategoryValue = submitData?.accreditationForm_subCategory;

  if (!Array.isArray(maxNumberOfApplicationsPerRole)) {
    return false;
  }

  const roleIndex = Number(subCategoryValue);

  if (!Number.isInteger(roleIndex) || roleIndex < 0) {
    return false;
  }

  const role =
    Object.values(ACCREDITATION_ROLES).find(role => role.value === roleIndex)
      ?.label || '';

  if (!role) {
    return false;
  }

  const matchingLimit = maxNumberOfApplicationsPerRole.find(item => {
    const limitRoleIndex = Number(item?.item?.value);
    return Number.isInteger(limitRoleIndex) && limitRoleIndex === roleIndex;
  });

  const maxForRole = Number(matchingLimit?.key);

  if (!Number.isFinite(maxForRole) || maxForRole < 0) {
    return false;
  }

  const currentCount = Number(currentApplicationsPerRoleCount?.[role] ?? 0);

  return currentCount + 1 > maxForRole;
};

export default handle;
